// src/services/analytics.js
//
// Analytics for Relationship Compass, on the fleet's consent-aware GA4 rail
// (@ddd/analytics — the profound-thinking pattern; see
// docs/ANALYTICS_CONSENT_ROLLOUT.md).
//
// The single source of truth for consent is the user's own `analytics`
// preference: set by the first-visit notice and changed on the Data &
// Privacy page. Nothing is measured until they say yes: @ddd/analytics
// injects the gtag loader only after consent, so before that the app makes
// no analytics network request of any kind.
//
// The measurement ID is a literal on purpose (public by nature; an env var
// silently kills analytics in any worktree without a .env). G-DSE9VP2NQN is
// the app's existing Firebase-linked GA4 stream (Wave 1 of the rollout),
// kept at Nigel's confirmation on 2026-09-27.
import { initAnalytics, setAnalyticsConsent, getStoredConsent } from '@ddd/analytics';

export const MEASUREMENT_ID = 'G-DSE9VP2NQN';

// This app had no preferences store before the rail, so the analytics
// decision gets its own small key. Read at boot, before React mounts.
export const PREFERENCES_KEY = 'relationshipCompass_preferences';
export const ANALYTICS_PREFERENCE_EVENT = 'relationshipCompass:analyticsPreference';

function readPreferences() {
  try {
    const stored = localStorage.getItem(PREFERENCES_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** The user's stored analytics decision. Defaults to false — opt-in, never opt-out. */
export function hasAnalyticsPreference() {
  return readPreferences().analytics === true;
}

/**
 * Record a decision from the consent notice or the privacy toggle. Writes the
 * preference immediately (merged over what's stored, so other keys survive),
 * then tells the rail — boot re-derives consent from this preference, so it
 * must never lag behind the rail's own record.
 */
export function setAnalyticsPreference(granted) {
  try {
    localStorage.setItem(
      PREFERENCES_KEY,
      JSON.stringify({ ...readPreferences(), analytics: Boolean(granted) })
    );
  } catch {
    /* storage unavailable — the in-page decision below still applies */
  }
  setAnalyticsConsent(Boolean(granted));
  // Keep an open Data & Privacy page's toggle in step with the notice.
  try {
    window.dispatchEvent(new CustomEvent(ANALYTICS_PREFERENCE_EVENT, { detail: Boolean(granted) }));
  } catch {
    /* no window (tests) */
  }
}

/**
 * Call once at startup, before React mounts. Loads GA4 only if the user has
 * already opted in; otherwise it arms the helper and waits for a decision.
 */
export function startAnalytics() {
  initAnalytics({ measurementId: MEASUREMENT_ID, appId: 'relationship-compass' });
  // Record a consent state only when a real decision exists. Writing
  // 'denied' for a visitor who has never been asked would erase the
  // never-asked state the first-visit consent notice keys on.
  if (getStoredConsent() !== null || hasAnalyticsPreference()) {
    setAnalyticsConsent(hasAnalyticsPreference());
  }
}
